import React, {useRef, useState} from 'react'
import {useMediaQuery} from "@mui/material";
import DHDialog from "../../../general/DHDialog";
import DHSelect from "../../../general/DHSelect";
import ErrorInfoComponent from "../../../general/ErrorInfoComponent";
import FetchUtil from "../../../../util/FetchUtil";
import ConfigUtil from "../../../../util/ConfigUtil";

const FARBE_DUNKEL = "#022836";
const FARBE_HELL = "#eaf1f5";

const ERGEBNISSE = {
    SIEG: {label: "Sieg", color: "#2e7d32"},
    UNENTSCHIEDEN: {label: "Unentschieden", color: "#ed6c02"},
    NIEDERLAGE: {label: "Niederlage", color: "#ac1c27"},
    FREILOS: {label: "Freilos", color: "#0277bd"},
};

const ZELLE = {padding: "6px 10px", borderBottom: "1px solid " + FARBE_HELL};

export default function SchweizerSystemSortierungDialog({tournamentId, players, onClose}) {

    const topRef = useRef(null);
    const isMobile = useMediaQuery('(max-width:900px)');

    const [errorMessage, setErrorMessage] = useState(undefined);
    const [infoMessage, setInfoMessage] = useState(undefined);
    const [selectedPlayerId, setSelectedPlayerId] = useState(undefined);
    const [protokoll, setProtokoll] = useState(undefined);
    const [loading, setLoading] = useState(false);

    const ladeProtokoll = (playerId) => {
        setErrorMessage(undefined);
        setLoading(true);
        FetchUtil.fetchGet(ConfigUtil.getConfig().resourceUrls.public + "/tournament/" + tournamentId + "/schweizersystem/protokoll/" + playerId,
            json => {
                setProtokoll(json);
                setLoading(false);
                if (topRef.current) {
                    topRef.current.scrollIntoView({behavior: "smooth"});
                }
            },
            responseNotOk => {
                setErrorMessage("Fehler beim Laden des Protokolls: " + responseNotOk.message);
                setLoading(false);
            },
            error => {
                setErrorMessage("Fehler beim Laden des Protokolls: " + error.message);
                setLoading(false);
            }
        );
    }

    const changePlayer = (playerId) => {
        setSelectedPlayerId(playerId);
        setProtokoll(undefined);
        if (playerId === null || playerId === undefined) {
            return;
        }
        ladeProtokoll(playerId);
    }

    const kopiereProtokoll = () => {
        navigator.clipboard.writeText(protokoll.protokoll.join("\n"))
            .then(() => setInfoMessage("Protokoll wurde in die Zwischenablage kopiert."))
            .catch(() => setErrorMessage("Protokoll konnte nicht kopiert werden."));
    }

    const getPlayerModel = () => {
        return [...players]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(player => ({id: player.id, value: player.name}));
    }

    const getErgebnis = (spiel) => {
        if (spiel.freilos) {
            return ERGEBNISSE.FREILOS;
        }
        let ergebnis = ERGEBNISSE[spiel.ergebnis];
        return ergebnis !== undefined ? ergebnis : {label: spiel.ergebnis, color: "gray"};
    }

    const isVorhanden = (value) => {
        return value !== null && value !== undefined;
    }

    const getWertZuKriterium = (kriterium) => {
        switch (kriterium) {
            case "Punkte":
                return protokoll.punkte;
            case "Buchholz":
                return protokoll.buchholz;
            case "Feinbuchholz":
                return protokoll.feinBuchholz;
            case "Gewonnene Spiele":
                return protokoll.gewonneneSpiele;
            case "Leg-Differenz":
                return protokoll.legDifferenz > 0 ? "+" + protokoll.legDifferenz : protokoll.legDifferenz;
            case "Gewonnene Legs":
                return protokoll.legsGewonnen;
            default:
                return undefined;
        }
    }

    const getSpieleSortiert = () => {
        return [...protokoll.spiele].sort((a, b) => a.runde - b.runde || a.spielnummer - b.spielnummer);
    }

    const getGegnerName = (spiel) => {
        if (spiel.freilos) {
            return "Freilos";
        }
        let name = spiel.gegnerInaktiv ? <s>{spiel.gegnerName}</s> : spiel.gegnerName;
        if (!isVorhanden(spiel.gegnerPlayerId)) {
            return name;
        }
        return <span style={{cursor: "pointer", textDecoration: "underline"}} title={"Protokoll von " + spiel.gegnerName + " anzeigen"}
                     onClick={() => changePlayer(spiel.gegnerPlayerId)}>{name}</span>
    }

    const getKachel = (label, value, hervorheben) => {
        return <div key={"key_kachel_" + label} style={{
            backgroundColor: hervorheben ? FARBE_DUNKEL : FARBE_HELL,
            color: hervorheben ? "white" : FARBE_DUNKEL,
            borderRadius: 6, padding: isMobile ? 8 : 10, minWidth: isMobile ? 0 : 110, textAlign: "center"
        }}>
            <div style={{fontSize: 12, opacity: 0.8}}>{label}</div>
            <div style={{fontSize: isMobile ? 18 : 22, fontWeight: "bold"}}>{value}</div>
        </div>
    }

    const getBadge = (text, color) => {
        return <span style={{backgroundColor: color, color: "white", borderRadius: 4, padding: "2px 8px", fontSize: 13, whiteSpace: "nowrap"}}>{text}</span>
    }

    const getUeberschrift = (text) => {
        return <div style={{fontWeight: "bold", fontSize: 16, color: FARBE_DUNKEL, marginTop: 20, marginBottom: 10}}>{text}</div>
    }

    const getKopfbereich = () => {
        return <div style={{backgroundColor: FARBE_DUNKEL, color: "white", borderRadius: 6, padding: 15}}>
            <div style={{fontSize: isMobile ? 18 : 22, fontWeight: "bold"}}>
                {protokoll.inaktiv ? <s>{protokoll.playerName}</s> : protokoll.playerName}
                {protokoll.inaktiv && <span style={{fontSize: 14, marginLeft: 10, opacity: 0.8}}>(inaktiv)</span>}
            </div>
            <div style={{marginTop: 5, fontSize: 16}}>
                Platz <b>{protokoll.platz}</b> von {protokoll.anzahlSpieler} in Gruppe {protokoll.gruppe}
            </div>
        </div>
    }

    const getWerte = () => {
        let kacheln = [
            getKachel("Punkte", protokoll.punkte, true),
        ];
        if (protokoll.buchholzWertungAktiv) {
            kacheln.push(getKachel("Buchholz", protokoll.buchholz, true));
            kacheln.push(getKachel("Feinbuchholz", protokoll.feinBuchholz, true));
        }
        kacheln.push(getKachel("S / U / N", protokoll.gewonneneSpiele + " / " + protokoll.unentschiedeneSpiele + " / " + protokoll.verloreneSpiele, false));
        kacheln.push(getKachel("Legs", protokoll.legsGewonnen + " : " + protokoll.legsVerloren, false));
        kacheln.push(getKachel("Leg-Differenz", getWertZuKriterium("Leg-Differenz"), false));
        if (isMobile) {
            return <div style={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 15}}>{kacheln}</div>
        }
        return <div style={{display: "flex", flexWrap: "wrap", gap: 10, marginTop: 15}}>{kacheln}</div>
    }

    // Auf dem Handy als kompakte Liste mit nummerierten Kreisen statt als Tabelle.
    const getSortierungMobil = () => {
        return <div>
            {protokoll.sortierung.map((kriterium, index) => {
                let wert = getWertZuKriterium(kriterium);
                return <div key={"key_sortierung_" + kriterium}
                            style={{display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid " + FARBE_HELL}}>
                    <span style={{
                        backgroundColor: FARBE_DUNKEL, color: "white", borderRadius: "50%", width: 24, height: 24, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: "bold"
                    }}>{index + 1}</span>
                    <span style={{flex: 1, minWidth: 0}}>{kriterium}</span>
                    <span style={{fontWeight: "bold", fontSize: 16, whiteSpace: "nowrap"}}>{isVorhanden(wert) ? wert : "-"}</span>
                </div>
            })}
        </div>
    }

    // Auf dem Handy wird die Punktevergabe als Liste dargestellt, ein Eintrag je Zeile.
    const getPunktevergabeMobil = () => {
        return <div>
            {protokoll.punktevergabeEintraege.map((eintrag, index) =>
                <div key={"key_punktevergabe_" + index}
                     style={{display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid " + FARBE_HELL}}>
                    <span style={{flex: 1, minWidth: 0}}>{eintrag.bezeichnung}</span>
                    {isVorhanden(eintrag.wert) && <span style={{fontWeight: "bold", textAlign: "right"}}>{eintrag.wert}</span>}
                </div>
            )}
        </div>
    }

    const getRegeln = () => {
        return <div style={{display: "flex", flexWrap: "wrap", gap: isMobile ? 0 : 30}}>
            <div style={{flex: isMobile ? "1 1 100%" : "1 1 300px"}}>
                {getUeberschrift("Sortierung der Tabelle")}
                {isMobile ? getSortierungMobil() :
                <table style={{borderCollapse: "collapse", width: "100%"}}>
                    <tbody>
                    {protokoll.sortierung.map((kriterium, index) => {
                        let wert = getWertZuKriterium(kriterium);
                        return <tr key={"key_sortierung_" + kriterium}>
                            <td style={{...ZELLE, width: 30, opacity: 0.6}}>{index + 1}.</td>
                            <td style={ZELLE}>{kriterium}</td>
                            <td style={{...ZELLE, textAlign: "right", fontWeight: "bold"}}>{isVorhanden(wert) ? wert : "-"}</td>
                        </tr>
                    })}
                    </tbody>
                </table>}
                {!protokoll.buchholzWertungAktiv &&
                    <div style={{marginTop: 5, opacity: 0.6, fontSize: 12}}>Die Buchholz-Wertung ist für dieses Turnier nicht aktiv.</div>
                }
            </div>
            <div style={{flex: isMobile ? "1 1 100%" : "1 1 300px"}}>
                {getUeberschrift("Punktevergabe")}
                {isMobile ? getPunktevergabeMobil() : <div>{protokoll.punktevergabe}</div>}
            </div>
        </div>
    }

    const getBerechnungWert = (label, value, fett) => {
        return <div style={{flex: 1, textAlign: "center"}}>
            <div style={{fontSize: 11, opacity: 0.6}}>{label}</div>
            <div style={{fontWeight: fett ? "bold" : "normal"}}>{value}</div>
        </div>
    }

    // Auf dem Handy wird jedes Spiel als eigene Zeile mit den Werten untereinander dargestellt, statt als breite Tabelle.
    const getBerechnungMobil = (spiele, buchholz) => {
        return <>
            {spiele.map(spiel => <div key={"key_berechnung_" + spiel.runde + "_" + spiel.spielnummer}
                                      style={{borderBottom: "1px solid " + FARBE_HELL, padding: "6px 0"}}>
                <div><span style={{opacity: 0.6}}>R{spiel.runde}</span> {getGegnerName(spiel)}</div>
                <div style={{display: "flex", marginTop: 3}}>
                    {getBerechnungWert("Punkte", "+" + spiel.punkte)}
                    {buchholz && getBerechnungWert("Buchholz", isVorhanden(spiel.gegnerPunkte) ? "+" + spiel.gegnerPunkte : "-")}
                    {buchholz && getBerechnungWert("Feinbuchholz", isVorhanden(spiel.gegnerBuchholz) ? "+" + spiel.gegnerBuchholz : "-")}
                </div>
            </div>)}
            <div style={{backgroundColor: FARBE_DUNKEL, color: "white", borderRadius: 6, padding: "6px 0", marginTop: 8}}>
                <div style={{textAlign: "center", fontWeight: "bold"}}>Summe</div>
                <div style={{display: "flex"}}>
                    {getBerechnungWert("Punkte", protokoll.punkte, true)}
                    {buchholz && getBerechnungWert("Buchholz", protokoll.buchholz, true)}
                    {buchholz && getBerechnungWert("Feinbuchholz", protokoll.feinBuchholz, true)}
                </div>
            </div>
        </>
    }

    const getBerechnung = () => {
        let spiele = getSpieleSortiert().filter(spiel => spiel.zaehltFuerWertung);
        let buchholz = protokoll.buchholzWertungAktiv;
        if (isMobile) {
            return <>
                {getUeberschrift("Berechnung")}
                {getBerechnungMobil(spiele, buchholz)}
                {buchholz &&
                    <div style={{marginTop: 5, opacity: 0.6, fontSize: 12}}>
                        Buchholz = Summe der aktuellen Punkte aller Gegner. Feinbuchholz = Summe der aktuellen Buchholzzahlen aller Gegner.
                    </div>
                }
            </>
        }
        let kopfZelle = {...ZELLE, backgroundColor: FARBE_DUNKEL, color: "white", textAlign: "center"};
        let summenZelle = {...ZELLE, fontWeight: "bold", textAlign: "center", borderTop: "2px solid " + FARBE_DUNKEL};
        return <>
            {getUeberschrift("Berechnung")}
            <div style={{overflowX: "auto"}}>
                <table style={{borderCollapse: "collapse", minWidth: 400}}>
                    <thead>
                    <tr>
                        <th style={kopfZelle}>Runde</th>
                        <th style={{...kopfZelle, textAlign: "left"}}>Gegner</th>
                        <th style={kopfZelle}>Punkte</th>
                        {buchholz && <th style={kopfZelle} title="Aktuelle Punkte des Gegners">Buchholz</th>}
                        {buchholz && <th style={kopfZelle} title="Aktuelle Buchholzzahl des Gegners">Feinbuchholz</th>}
                    </tr>
                    </thead>
                    <tbody>
                    {spiele.map(spiel => <tr key={"key_berechnung_" + spiel.runde + "_" + spiel.spielnummer}>
                        <td style={{...ZELLE, textAlign: "center"}}>{spiel.runde}</td>
                        <td style={ZELLE}>{getGegnerName(spiel)}</td>
                        <td style={{...ZELLE, textAlign: "center"}}>+{spiel.punkte}</td>
                        {buchholz && <td style={{...ZELLE, textAlign: "center"}}>{isVorhanden(spiel.gegnerPunkte) ? "+" + spiel.gegnerPunkte : "-"}</td>}
                        {buchholz && <td style={{...ZELLE, textAlign: "center"}}>{isVorhanden(spiel.gegnerBuchholz) ? "+" + spiel.gegnerBuchholz : "-"}</td>}
                    </tr>)}
                    <tr>
                        <td style={summenZelle}></td>
                        <td style={{...summenZelle, textAlign: "left"}}>Summe</td>
                        <td style={summenZelle}>{protokoll.punkte}</td>
                        {buchholz && <td style={summenZelle}>{protokoll.buchholz}</td>}
                        {buchholz && <td style={summenZelle}>{protokoll.feinBuchholz}</td>}
                    </tr>
                    </tbody>
                </table>
            </div>
            {buchholz &&
                <div style={{marginTop: 5, opacity: 0.6, fontSize: 12}}>
                    Buchholz = Summe der aktuellen Punkte aller Gegner. Feinbuchholz = Summe der aktuellen Buchholzzahlen aller Gegner.
                </div>
            }
        </>
    }

    const getAufklappbereich = (titel, zeilen) => {
        if (!zeilen || zeilen.length === 0) {
            return "";
        }
        return <details style={{marginTop: 5}}>
            <summary style={{cursor: "pointer", fontSize: 14}}>{titel}</summary>
            <ul style={{margin: "5px 0 0 0", paddingLeft: 25, fontSize: 13}}>
                {zeilen.map((zeile, index) => <li key={"key_zeile_" + index} style={{padding: 1}}>{zeile}</li>)}
            </ul>
        </details>
    }

    const getSpiel = (spiel) => {
        let ergebnis = getErgebnis(spiel);
        let buchholz = protokoll.buchholzWertungAktiv;
        return <div key={"key_spiel_" + spiel.runde + "_" + spiel.spielnummer} style={{
            border: "1px solid " + FARBE_HELL, borderLeft: "6px solid " + ergebnis.color, borderRadius: 6,
            padding: 10, marginBottom: 10, opacity: spiel.zaehltFuerWertung ? 1 : 0.6
        }}>
            {isMobile ? <>
                <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6}}>
                    <span style={{fontWeight: "bold"}}>Runde {spiel.runde}</span>
                    <span style={{opacity: 0.6, fontSize: 12, flex: 1}}>Spiel {spiel.spielnummer}</span>
                    {getBadge(ergebnis.label, ergebnis.color)}
                    {getBadge("+" + spiel.punkte + " Pkt.", FARBE_DUNKEL)}
                </div>
                <div style={{display: "flex", alignItems: "center", gap: 10, marginTop: 6}}>
                    <span style={{flex: 1, fontSize: 15, minWidth: 0, overflowWrap: "anywhere"}}>
                        {spiel.freilos ? "Freilos" : <>gegen <b>{getGegnerName(spiel)}</b></>}
                    </span>
                    {!spiel.freilos && <span style={{fontSize: 18, fontWeight: "bold", whiteSpace: "nowrap"}}>{spiel.legsEigene} : {spiel.legsGegner}</span>}
                </div>
            </> :
            <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10}}>
                <span style={{fontWeight: "bold"}}>Runde {spiel.runde}</span>
                <span style={{opacity: 0.6, fontSize: 12}}>Spiel {spiel.spielnummer}</span>
                <span style={{flex: 1, fontSize: 16}}>
                    {spiel.freilos ? "Freilos" : <>gegen <b>{getGegnerName(spiel)}</b></>}
                </span>
                {!spiel.freilos && <span style={{fontSize: 18, fontWeight: "bold"}}>{spiel.legsEigene} : {spiel.legsGegner}</span>}
                {getBadge(ergebnis.label, ergebnis.color)}
                {getBadge("+" + spiel.punkte + " Pkt.", FARBE_DUNKEL)}
            </div>}
            {!spiel.zaehltFuerWertung &&
                <div style={{marginTop: 5, fontSize: 12, color: "#ac1c27"}}>Dieses Spiel zählt nicht für die Wertung.</div>
            }
            <div style={{marginTop: 8, fontSize: 14}}>
                <div>{spiel.punkteBegruendung}</div>
                {buchholz && !spiel.freilos && (isVorhanden(spiel.gegnerPunkte) || isVorhanden(spiel.gegnerBuchholz)) &&
                    <div style={{marginTop: 5, display: "flex", flexWrap: "wrap", gap: 15, opacity: 0.8}}>
                        {isVorhanden(spiel.gegnerPunkte) && <span>Punkte {spiel.gegnerName}: <b>{spiel.gegnerPunkte}</b> → Buchholz +{spiel.gegnerPunkte}</span>}
                        {isVorhanden(spiel.gegnerBuchholz) && <span>Buchholz {spiel.gegnerName}: <b>{spiel.gegnerBuchholz}</b> → Feinbuchholz +{spiel.gegnerBuchholz}</span>}
                    </div>
                }
                {buchholz && spiel.freilos && spiel.buchholzBegruendung &&
                    <div style={{marginTop: 5, opacity: 0.8}}>{spiel.buchholzBegruendung}</div>
                }
                {buchholz && getAufklappbereich("So kommt " + spiel.gegnerName + " auf " + spiel.gegnerPunkte + " Punkte", spiel.gegnerSpiele)}
                {buchholz && getAufklappbereich("So kommt " + spiel.gegnerName + " auf die Buchholzzahl " + spiel.gegnerBuchholz, spiel.gegnerBuchholzBestandteile)}
            </div>
        </div>
    }

    const getSpiele = () => {
        let spiele = getSpieleSortiert();
        return <>
            {getUeberschrift("Spiele")}
            {spiele.length === 0 && <div style={{opacity: 0.6}}>Keine Spiele vorhanden.</div>}
            {spiele.map(spiel => getSpiel(spiel))}
        </>
    }

    const getTextprotokoll = () => {
        if (!protokoll.protokoll || protokoll.protokoll.length === 0) {
            return "";
        }
        return <details style={{marginTop: 20}}>
            <summary style={{cursor: "pointer", fontWeight: "bold", fontSize: 16}}>Komplettes Protokoll als Text</summary>
            <div style={{marginTop: 10}}>
                <button type="button" onClick={() => kopiereProtokoll()} style={{cursor: "pointer", marginBottom: 5}}>
                    <i className="pi pi-copy" style={{marginRight: 5}}/>Kopieren
                </button>
                <pre style={{backgroundColor: FARBE_HELL, padding: 10, borderRadius: 6, fontSize: 13, whiteSpace: "pre-wrap", margin: 0}}>
                    {protokoll.protokoll.join("\n")}
                </pre>
            </div>
        </details>
    }

    return <DHDialog onClose={onClose} title="Schweizer System - Sortierung" showCloseButton={true} buttons={[]} comp={<>
        <ErrorInfoComponent infoMessage={infoMessage} errorMessage={errorMessage} onClearInfoMessage={() => setInfoMessage(undefined)} onClearErrorMessage={() => setErrorMessage(undefined)}/>
        <div ref={topRef} style={{marginBottom: 10}}>
            <DHSelect id="id-schweizer-sortierung-spieler" label="Spieler" width={300}
                      value={selectedPlayerId}
                      model={getPlayerModel()}
                      onChange={(id) => changePlayer(id)}/>
        </div>
        {loading && <div>Lade Daten...</div>}
        {protokoll !== undefined && <div style={{color: FARBE_DUNKEL, maxWidth: 1000}}>
            {getKopfbereich()}
            {getWerte()}
            {getRegeln()}
            {getBerechnung()}
            {getSpiele()}
            {getTextprotokoll()}
        </div>}
    </>}/>
}
